import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper, // Added Paper for card layout
  Divider, // Added Divider for visual separation
  Alert, // For displaying errors cleanly
} from "@mui/material";
import {
  Save,
  Edit,
  Delete,
  Settings,
  ListAlt,
  MonetizationOn,
} from "@mui/icons-material"; // Added Icons
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js";
import Pagination from "../components/Pagination.js"; // Assuming this path is correct

// --- Strict ERP Color Palette ---
const PRIMARY_BLUE = "#2E4053";
const DARK_TEXT = "#1E293B";
const LIGHT_BG = "#F7F9FC";
const CARD_BG = "#FFFFFF";
const SECONDARY_TEXT = "#64748B";

const RewardSetting = () => {
  const [rewardSettings, setRewardSettings] = useState([]);
  const [products, setProducts] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [rewardData, setRewardData] = useState({
    points_per_currency: 0,
    type: "per_price",
    product_id: "",
    expiry_period_months: 6,
    effective_date: new Date().toISOString().split("T")[0], // Set default date format for input
    isActive: 1,
  });
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Helper function to reset form state
  const resetForm = () => {
    setRewardData({
      points_per_currency: 0,
      type: "per_price",
      product_id: "",
      expiry_period_months: 6,
      effective_date: new Date().toISOString().split("T")[0],
      isActive: 1,
    });
    setIsEditMode(false);
    setError(null);
  };

  // Fetch all reward settings and products
  const fetchRewardSettings = async (page = 1) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const { data: rewardsettings, pagination } = await fetchDataFromAPI(
        "/api/rewardsettings",
        { page, limit: itemsPerPage, ...filters },
        token
      );
      setRewardSettings(rewardsettings || []);
      setTotalPages(pagination?.totalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching reward settings:", err.message);
      setError("Failed to load reward settings.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetchDataFromAPI("/api/products", {}, token);
      if (Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("Error fetching products:", err.message);
    }
  };

  useEffect(() => {
    fetchRewardSettings(currentPage);
    fetchProducts();
  }, [filters, currentPage, itemsPerPage]); // Added itemsPerPage dependency

  // Handle changes in the form inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRewardData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  // Handle submit (Create or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const token = localStorage.getItem("token");

    // Validation
    if (rewardData.type === "per_product" && !rewardData.product_id) {
      return setError("Product is required when type is 'Per Product'");
    }

    try {
      if (isEditMode) {
        // Update an existing reward setting
        const response = await saveData(
          `/api/rewardsettings/${rewardData.id}`,
          rewardData,
          "PUT",
          token
        );
        alert("Reward setting updated successfully.");
      } else {
        // Create a new reward setting
        const response = await saveData(
          "/api/rewardsettings",
          rewardData,
          "POST",
          token
        );
        alert("Reward setting created successfully.");
      }

      // Refresh list and reset form
      fetchRewardSettings(currentPage);
      resetForm();
    } catch (err) {
      console.error("Error submitting reward setting:", err.message);
      setError(`Failed to save reward setting: ${err.message}`);
    }
  };

  // Handle editing of a reward setting
  const handleEdit = (reward) => {
    // Ensure effective_date is correctly formatted for the input type="date"
    const dateValue = reward.effective_date
      ? new Date(reward.effective_date).toISOString().split("T")[0]
      : "";
    setRewardData({
      ...reward,
      effective_date: dateValue,
      isActive: reward.isActive === 1, // Normalize for Switch component
    });
    setIsEditMode(true);
    setError(null);
  };

  // Handle deleting a reward setting
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this reward setting?"))
      return;

    const token = localStorage.getItem("token");
    try {
      await deleteData(`/api/rewardsettings/${id}`, token);

      // Update local state and refresh list
      setRewardSettings((prevState) =>
        prevState.filter((item) => item?.id !== id)
      );
      alert("Reward setting deleted successfully.");
      // If the last item on a page is deleted, move to the previous page
      if (rewardSettings.length === 1 && currentPage > 1) {
        fetchRewardSettings(currentPage - 1);
      } else {
        fetchRewardSettings(currentPage);
      }
    } catch (err) {
      console.error("Error deleting reward setting:", err.message);
      setError(`Failed to delete reward setting: ${err.message}`);
    }
  };

  // Handle page change
  const onPageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ padding: "30px", backgroundColor: LIGHT_BG, minHeight: "100vh" }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: DARK_TEXT,
          mb: 4,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Settings sx={{ mr: 1, color: PRIMARY_BLUE }} /> Reward Settings
        Management
      </Typography>

      {/* --- 1. Form Section (Card Layout) --- */}
      <Paper elevation={3} sx={{ padding: "30px", borderRadius: "8px", mb: 5 }}>
        <Typography
          variant="h6"
          sx={{
            color: PRIMARY_BLUE,
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <MonetizationOn sx={{ mr: 1 }} />{" "}
          {isEditMode ? "Edit Reward Rule" : "Create New Reward Rule"}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Points per Currency */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Points per Currency"
                name="points_per_currency"
                type="number"
                variant="outlined"
                value={rewardData.points_per_currency}
                onChange={handleInputChange}
                required
              />
            </Grid>

            {/* Type Selection */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Rule Type</InputLabel>
                <Select
                  name="type"
                  label="Rule Type"
                  value={rewardData.type}
                  onChange={handleInputChange}
                >
                  <MenuItem value="per_price">Per Price (Standard)</MenuItem>
                  <MenuItem value="per_product">
                    Per Product (Specific)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Product ID Selection (Conditional) */}
            {rewardData.type === "per_product" && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Select Product</InputLabel>
                  <Select
                    name="product_id"
                    label="Select Product"
                    value={rewardData.product_id}
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {products?.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Expiry Period */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Expiry Period (Months)"
                name="expiry_period_months"
                type="number"
                variant="outlined"
                value={rewardData.expiry_period_months}
                onChange={handleInputChange}
                required
              />
            </Grid>

            {/* Effective Date (Added date field for clarity) */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Effective Date"
                name="effective_date"
                type="date"
                variant="outlined"
                value={rewardData.effective_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Active Switch */}
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={rewardData.isActive === 1}
                    onChange={handleInputChange}
                    name="isActive"
                    color="primary" // Use primary color for switch
                  />
                }
                label={
                  <Typography color={DARK_TEXT}>Rule is Active</Typography>
                }
                labelPlacement="start"
              />
            </Grid>

            {/* Submit Button */}
            <Grid
              item
              xs={12}
              sx={{
                pt: 3,
                display: "flex",
                gap: 2,
                justifyContent: "flex-start",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: PRIMARY_BLUE,
                  "&:hover": { backgroundColor: "#1A2A38" },
                  color: "#fff",
                  fontWeight: 600,
                  padding: "10px 20px",
                }}
                type="submit"
                startIcon={<Save />}
              >
                {isEditMode ? "Update Rule" : "Create Rule"}
              </Button>
              {isEditMode && (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{
                    color: PRIMARY_BLUE,
                    borderColor: PRIMARY_BLUE,
                    "&:hover": { backgroundColor: "#F0F3F7" },
                    fontWeight: 600,
                  }}
                >
                  Cancel Edit
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* --- 2. List Section --- */}
      <Box mt={4}>
        <Typography
          variant="h5"
          sx={{
            color: DARK_TEXT,
            mb: 3,
            display: "flex",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          <ListAlt sx={{ mr: 1, color: PRIMARY_BLUE }} /> Current Reward Rules
        </Typography>

        {loading && (
          <Typography sx={{ p: 2, color: SECONDARY_TEXT }}>
            Loading list...
          </Typography>
        )}

        <Grid container spacing={3}>
          {rewardSettings?.map((setting) => (
            <Grid item xs={12} sm={6} md={4} key={setting?.id}>
              <Paper
                elevation={1}
                sx={{
                  borderLeft:
                    setting?.isActive === 1
                      ? `4px solid ${PRIMARY_BLUE}`
                      : "4px solid #D32F2F", // Status indicator
                  borderRadius: "4px",
                  padding: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  backgroundColor: CARD_BG,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={DARK_TEXT}
                >
                  Rule ID: {setting?.id}
                </Typography>
                <Divider />

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" color={SECONDARY_TEXT}>
                    **Type:**{" "}
                    {setting?.type === "per_price"
                      ? "Standard (Per Price)"
                      : "Specific (Per Product)"}
                  </Typography>
                  {setting?.product_id && (
                    <Typography variant="body2" color={SECONDARY_TEXT}>
                      **Product ID:** {setting.product_id}
                    </Typography>
                  )}
                  <Typography variant="body2" color={SECONDARY_TEXT}>
                    **Points/Currency:**{" "}
                    <Box component="span" fontWeight="bold">
                      {setting?.points_per_currency}
                    </Box>
                  </Typography>
                  <Typography variant="body2" color={SECONDARY_TEXT}>
                    **Expires:** {setting?.expiry_period_months} months
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: "bold",
                      color: setting?.isActive === 1 ? PRIMARY_BLUE : "#D32F2F",
                    }}
                  >
                    {setting?.isActive === 1
                      ? "STATUS: ACTIVE"
                      : "STATUS: INACTIVE"}
                  </Typography>

                  <div>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleEdit(setting)}
                      sx={{
                        color: PRIMARY_BLUE,
                        "&:hover": { backgroundColor: "#F0F3F7" },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(setting.id)}
                      sx={{
                        color: "#D32F2F",
                        ml: 1,
                        "&:hover": { backgroundColor: "#FEEAE9" },
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* --- 3. Pagination Section --- */}
      <Box
        sx={{
          marginTop: "30px",
          padding: "15px",
          display: "flex",
          justifyContent: "center",
          backgroundColor: CARD_BG,
          borderRadius: "4px",
          boxShadow: 1,
        }}
      >
        <Pagination
          totalItem={rewardSettings.length}
          itemPerPage={itemsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          fetchItems={fetchRewardSettings}
        />
      </Box>
    </Container>
  );
};

export default RewardSetting;
